import { Inject } from '@odinjs/odin'

import { ActionAPI, DeckAPI, ModalService, PanelAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { HttpService, } from '@philips-internal/oswald-ui-core/lib/services';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';

import { OralSupplementsType, PrescriptionPosition } from './oral-supplements-type';
import { Function9129Param13Values, OralSupplementsStatusItem } from './oral-supplements-params';
import BaseController from '../../../BaseController';
import { MedicationAndDateHeaderParams } from '../../../commons/header/medication-and-date-header-params';
import { Endpoint } from '../../../../constants/endpoints';
import { FragmentNames, OralSupplementsViewerAliases } from '../../../../codegen';
import InformationBarUtils from '../../../../controllers/commons/header/information-bar-header-utils';
import ShowToast from '../../../../utils/toast-service';
import { FoundationUtils } from '../../../../utils/foundation-utils';
import { ADEP_E1113Parameters, ADEP_E9129Parameters, FunctionName } from '../../../../utils/parameters';
import { ApplicationRoute } from '../../../../constants/routes';
import { YesNoType } from '../../../../utils/enum-utils';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { MedicineAndSolutionsParams } from '../medicine-and-solutions/medicine-and-solutions-params';
import { HealthProfessionalUser } from 'src/app/authentication';
import { MedicationViewerParams } from '../medication-viewer/medication-viewer-params';
import { MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { Params } from '../administration-schedule/administration-schedule-types';

@Controller({ component: OralSupplementsViewerAliases.ORAL_SUPPLEMENTS_VIEWER_LAYOUT, fragment: FragmentNames.ORAL_SUPPLEMENTS_VIEWER })
class OralSupplementsViewerController extends BaseController {

  @Inject(OralSupplementsViewerAliases.ORAL_SUPPLEMENTS_VIEWER_DECK)
  private deckApi!: DeckAPI;

  @Inject(OralSupplementsViewerAliases.ORAL_SUPPLEMENTS_PANEL_BUTTON)
  private panelExecuteApi!: PanelAPI;

  @OnMount(OralSupplementsViewerAliases.ORAL_SUPPLEMENTS_VIEWER_LAYOUT)
  handleMountLayout(): void {
    InformationBarUtils.collapsedBar = true;
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();

    this.context.set({
      encounterId,
      patientId,
    });
  }

  @OnMount(OralSupplementsViewerAliases.SUSPEND_ORAL_SUPPLEMENTS_ACTION)
  handleMountSuspendOralSupplementsAction(event: MountEvent): void {
    const actionApi = event.target as ActionAPI;
    actionApi.setEnabled(this.shouldShowSuspendMedicationAction() && !this.wasAdministered())
  }

  @OnClick(OralSupplementsViewerAliases.SUSPEND_ORAL_SUPPLEMENTS_ACTION)
  handleClickSuspendOralSupplementsAction(): void {
    const { patientId, encounterId } = this.context.getBulkData() as Record<string, string>;
    applicationContext.redirect(ApplicationRoute.ADEP_SUSPEND_ITEM, {
      encounterId,
      patientId,
    });
  }

  @OnMount(OralSupplementsViewerAliases.RESCHEDULE_ORAL_SUPPLEMENTS_ACTION)
  handleMountRescheduleAction(event: MountEvent): void {
    const actionApi = event.target as ActionAPI;
    const allowReschedule = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_RESCHEDULE_MEDICATION);
    actionApi.setEnabled(allowReschedule === Params.ALLOWED && !this.wasAdministered());
  }

  @OnClick(OralSupplementsViewerAliases.RESCHEDULE_ORAL_SUPPLEMENTS_ACTION)
  handleClickRescheduleOralSupplementsAction(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    this.addNewParams();
    const route = ApplicationRoute.EMAR_SCHEDULE_MEDICATION_INFUSION;
    applicationContext.redirect(route, { encounterId, patientId });
  }

  private addNewParams(): void {
    const data = MedicineAndSolutionsParams?.params?.startId ? MedicineAndSolutionsParams.params.startId : '';

    MedicationViewerParams.params = {
      eventDate: data,
      hourSequence: MedicineAndSolutionsParams.params.hourSequence,
      itemCode: MedicineAndSolutionsParams.params.cdMaterialId,
      medicationName: MedicineAndSolutionsParams.params.dsMaterialId,
      prescriptionNumber: MedicineAndSolutionsParams.params.nrPrescriptionId,
    }
  }

  @OnMount(OralSupplementsViewerAliases.ORAL_SUPPLEMENTS_VIEWER_DECK)
  handleMountDeck(): void {
    this.deckApi.setDone(true);
    this.deckApi.setLoading(true);

    this.fetchOralSupplements().then((response) => {
      const responseBody = response as OralSupplementsType;

      MedicationViewerParams.adminSchedule = {
        dilutionDescription: responseBody.dilutionDescription,
        materialSequence: responseBody.materialSequence,
        medicationHour: responseBody.scheduleDate,
        prescriptionNumber: responseBody.prescriptionNumber,
      };

      MedicationAndDateHeaderParams.medicationAndDateHeaderProps = {
        medicationName: responseBody.materialDescription,
        scheduleDate: responseBody.scheduleDate,
        type: 'ORAL_SUPPLEMENTS'
      };

      MedicineAndSolutionsParams.params = {
        ...MedicineAndSolutionsParams.params,
        amountAdministered: responseBody.doseQuantity,
        cdIntervalId: responseBody.intervalCode,
        ieViaApplicationDescription: responseBody.routeAdministrationDescription,
        unitMeasureDoseDescription: responseBody.unitMeasureDoseDescription,
      }

      this.deckApi.setData([{ ...responseBody, prescriptionPosition: PrescriptionPosition.LEFT }]);
      this.deckApi.setLoading(false);
    }).catch((error) => {
      if (error.body) {
        ShowToast.open(error.body.errorMessage, ToastLevel.ERROR)
      }
    });
  }

  private fetchOralSupplements(): Promise<OralSupplementsType> {
    const { scheduleSequenceNumber } = this.historyService.getCurrentRouteParams();

    return HttpService.get<OralSupplementsType>(Endpoint.EMAR_GET_ORAL_SUPPLEMENTS_INFORMATION, {
      scheduleSequenceNumber,
    }).then((response) => {
      const responseBody = response.body as OralSupplementsType;
      this.context.set({ ...responseBody });
      this.panelExecuteApi.setVisible(!this.wasAdministered() && this.isAllowedToPerformItems());
      return responseBody;
    })
  }

  @OnClick(OralSupplementsViewerAliases.ORAL_SUPPLEMENTS_EXECUTE_BUTTON)
  onClickExecuteButton(): void {
    ModalService.openDefault({
      contextData: this.context.getBulkData() as Record<string, Value>,
      fragment: FragmentNames.ORAL_SUPPLEMENTS_EXECUTE_TIME_MODAL,
      id: FragmentNames.ORAL_SUPPLEMENTS_EXECUTE_TIME_MODAL,
      title: 'text.emar-administration',
    })
  }

  private shouldShowSuspendMedicationAction(): boolean {
    const allowMedicalUsersSuspendSchedule = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_MEDICAL_USERS_SUSPEND_SCHEDULES);
    const user = foundationContext.getUser() as HealthProfessionalUser;
    if (user.isDoctor) {
      return allowMedicalUsersSuspendSchedule !== YesNoType.NO;
    }
    const allowOnlyRevert = 'R';
    const allowNonMedicalSuspendOrReverseSuspension = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_NON_MEDICAL_SUSPEND_OR_REVERSE_SUSPENSIONS);

    return allowNonMedicalSuspendOrReverseSuspension !== YesNoType.NO.valueOf() && allowNonMedicalSuspendOrReverseSuspension !== allowOnlyRevert;
  }

  private wasAdministered(): boolean {
    const { statusItem } = this.context.getBulkData();
    return [OralSupplementsStatusItem.ADMINISTERED, OralSupplementsStatusItem.SUPPLEMENTS_ADMINISTERED].includes(statusItem as string);
  }

  private isAllowedToPerformItems(): boolean {
    const param13Value = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.ALLOWS_TO_PERFORM_PRESCRIPTION_ITEMS) as string;
    return [Function9129Param13Values.YES, Function9129Param13Values.DEPENDS_ON_THE_RULE].includes(param13Value);
  }
}

export default OralSupplementsViewerController;
